/**
 * Europe/Ljubljana time helpers shared by client + server (Nova Gorica MONG pilot).
 *
 * Everything the NG pilot shows or exports is pinned to Europe/Ljubljana,
 * regardless of the host/browser timezone — so the on-screen table, the ECharts
 * axis, and the CSV/XLSX exports all agree. Pure + dependency-free (uses Intl,
 * available in Node 24 and modern browsers).
 *
 * Used by: NgArsoPanel, NgPrometPanel (display + daily grouping), and the
 * NgArso/NgPromet export services (CSV/XLSX timestamps).
 */

const TZ = 'Europe/Ljubljana';

interface LjubljanaParts {
  year: string;
  month: string;
  day: string;
  hour: string;
  minute: string;
  second: string;
}

// One formatter for the module: building an Intl.DateTimeFormat is the
// expensive part (a month of hourly rows × 22 sensors took over a second
// when every call built its own, #715).
const PARTS_FORMAT = new Intl.DateTimeFormat('en-GB', {
  timeZone: TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

/**
 * Wall-clock parts of an instant in Europe/Ljubljana, zero-padded. Built from
 * `formatToParts` (not `.format()`) so the output is deterministic regardless
 * of locale separators — important because these strings land in CSV cells.
 */
function partsLjubljana(d: Date): LjubljanaParts {
  const parts = PARTS_FORMAT.formatToParts(d);
  const get = (t: Intl.DateTimeFormatPartTypes): string =>
    parts.find((p) => p.type === t)?.value ?? '';
  let hour = get('hour');
  if (hour === '24') hour = '00'; // some engines emit '24' at midnight with hour12:false
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour,
    minute: get('minute'),
    second: get('second'),
  };
}

/**
 * Format an ISO/epoch-parseable timestamp as Europe/Ljubljana wall-clock text:
 *   - 'date'            -> 'YYYY-MM-DD'
 *   - 'datetime'        -> 'YYYY-MM-DD HH:mm'
 *   - 'datetimeSeconds' -> 'YYYY-MM-DD HH:mm:ss' (where two events of one
 *     minute must stay apart — the login mail's request time, #668)
 * ISO-like + sortable. Returns the input unchanged if it can't be parsed.
 *
 * Display label + daily grouping key ONLY — it formats an instant to text, it
 * does NOT yield an instant (see {@link ljubljanaDayStartIso}).
 */
export function formatLjubljanaTs(iso: string, granularity: 'date' | 'datetime' | 'datetimeSeconds'): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return iso;
  const p = partsLjubljana(d);
  const date = `${p.year}-${p.month}-${p.day}`;
  if (granularity === 'date') return date;
  const minutes = `${date} ${p.hour}:${p.minute}`;
  return granularity === 'datetimeSeconds' ? `${minutes}:${p.second}` : minutes;
}

const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;

/** One run of wall-clock time that the autumn clock change repeats: [from, change) in summer time, [change, to) in standard time. */
interface RepeatedSpan {
  from: number;
  change: number;
  to: number;
}

// Per UTC year; found once from the zone data, so a long export pays two
// comparisons per row, not three more formatToParts calls (#1103).
const repeatedSpansByYear = new Map<number, RepeatedSpan[]>();

function repeatedSpans(year: number): RepeatedSpan[] {
  const cached = repeatedSpansByYear.get(year);
  if (cached) return cached;
  const spans: RepeatedSpan[] = [];
  const end = Date.UTC(year + 1, 0, 1);
  let dayOffset = ljubljanaOffsetMinutes(Date.UTC(year, 0, 1));
  for (let day = Date.UTC(year, 0, 1); day < end; day += DAY_MS) {
    const nextOffset = ljubljanaOffsetMinutes(day + DAY_MS);
    if (nextOffset < dayOffset) {
      // The clocks went back during this UTC day: find the hour (Ljubljana
      // changes at whole UTC hours) and the size of the step.
      for (let h = day + HOUR_MS; h <= day + DAY_MS; h += HOUR_MS) {
        const before = ljubljanaOffsetMinutes(h - HOUR_MS);
        const after = ljubljanaOffsetMinutes(h);
        if (after < before) {
          const back = (before - after) * 60_000;
          spans.push({ from: h - back, change: h, to: h + back });
          break;
        }
      }
    }
    dayOffset = nextOffset;
  }
  repeatedSpansByYear.set(year, spans);
  return spans;
}

/**
 * The zone of an instant that falls in the hour the autumn clock change
 * repeats — 'CEST' for its first run, 'CET' for its second — else null (also
 * for an unparseable input). On 25. 10. 2026: 00:00Z–01:00Z is 02:00 CEST,
 * 01:00Z–02:00Z is 02:00 CET. The spring change repeats nothing: null.
 *
 * Ljubljana only ever steps back from summer time to standard time, so the
 * two names are fixed.
 */
export function ljubljanaRepeatedHourZone(iso: string): 'CEST' | 'CET' | null {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return null;
  for (const s of repeatedSpans(new Date(t).getUTCFullYear())) {
    if (t >= s.from && t < s.change) return 'CEST';
    if (t >= s.change && t < s.to) return 'CET';
  }
  return null;
}

/**
 * 'YYYY-MM-DD HH:mm' in Europe/Ljubljana, with » CEST« / » CET« on the hour
 * the autumn clock change repeats — and only there, so the two 02:00 of that
 * night are told apart and every other hour prints as `formatLjubljanaTs(iso,
 * 'datetime')` (#1103). For the hourly times of the NG views, charts and exports.
 */
export function formatLjubljanaHour(iso: string): string {
  const text = formatLjubljanaTs(iso, 'datetime');
  const zone = ljubljanaRepeatedHourZone(iso);
  return zone ? `${text} ${zone}` : text;
}

/**
 * Minutes that Europe/Ljubljana is ahead of UTC at the given instant
 * (+60 in winter/CET, +120 in summer/CEST).
 */
function ljubljanaOffsetMinutes(utcMs: number): number {
  const p = partsLjubljana(new Date(utcMs));
  const asUtc = Date.UTC(
    Number(p.year),
    Number(p.month) - 1,
    Number(p.day),
    Number(p.hour),
    Number(p.minute),
    Number(p.second),
  );
  return Math.round((asUtc - utcMs) / 60_000);
}

/**
 * Given a 'YYYY-MM-DD' Europe/Ljubljana calendar day, return the UTC ISO
 * instant of that day's local midnight (DST-correct). Lets a daily bucket keyed
 * by the local day be emitted at its true boundary rather than a UTC-midnight
 * stand-in (`${day}T00:00:00Z`, which is 02:00 Ljubljana under CEST).
 *
 * Probes the zone offset at the naive midnight, applies it, then re-checks once
 * so the two DST-transition days a year resolve correctly.
 */
export function ljubljanaDayStartIso(dayKey: string): string {
  return ljubljanaHourStartIso(dayKey, 0);
}

/**
 * The UTC ISO instant at which the given hour of day (0–23) starts on a
 * 'YYYY-MM-DD' Europe/Ljubljana calendar day (DST-correct; same probe as the
 * day start, which is its hour 0).
 *
 * The two clock-change nights: the hour that occurs twice in autumn (02:00)
 * resolves to the SECOND one — what PostgreSQL's `AT TIME ZONE` and so the
 * promet zone route answer for the same day + hour; the hour that does not
 * exist in spring (02:00) resolves to the next real hour (03:00).
 */
export function ljubljanaHourStartIso(dayKey: string, hour: number): string {
  const parts = dayKey.split('-');
  const y = Number(parts[0]);
  const m = Number(parts[1]);
  const d = Number(parts[2]);
  const naiveUtc = Date.UTC(y, m - 1, d, hour, 0, 0);
  const off1 = ljubljanaOffsetMinutes(naiveUtc);
  let candidate = naiveUtc - off1 * 60_000;
  const off2 = ljubljanaOffsetMinutes(candidate);
  if (off2 !== off1) candidate = naiveUtc - off2 * 60_000;
  return new Date(candidate).toISOString();
}

/**
 * Whole Europe/Ljubljana calendar days from `then` to `now` (0 = same local day,
 * 1 = yesterday). NaN if either instant is unparseable.
 *
 * Calendar days, not elapsed hours: something posted at 23:50 must read
 * "včeraj" at 00:10 the next morning, not "pred 20 minutami". Anchors each day
 * at UTC noon before subtracting, because anchoring at midnight lets a DST
 * transition round the difference to the wrong integer twice a year.
 */
export function ljubljanaDayDiff(then: Date, now: Date): number {
  const noonOf = (d: Date): number => {
    const key = formatLjubljanaTs(d.toISOString(), 'date');
    const y = Number(key.slice(0, 4));
    const m = Number(key.slice(5, 7));
    const day = Number(key.slice(8, 10));
    if (!Number.isInteger(y) || !Number.isInteger(m) || !Number.isInteger(day)) return NaN;
    return Date.UTC(y, m - 1, day, 12);
  };
  if (!Number.isFinite(then.getTime()) || !Number.isFinite(now.getTime())) return NaN;
  const a = noonOf(then);
  const b = noonOf(now);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return NaN;
  return Math.round((b - a) / 86_400_000);
}
